"use client";

import { useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { tournamentApi } from "@/shared/api/tournament";
import { useChampions } from "@/features/predraft/model/use-champions";
import { Skeleton } from "@/shared/ui/data-display/skeleton";
import { RulebookDisplayCards } from "./rulebook-display-cards";

const rulebookQueryKey = (tournamentId: string) => ["tournament", tournamentId, "rulebook"] as const;

type Rulebook = Awaited<ReturnType<typeof tournamentApi.getRulebook>>;

function rulebookHasContent(rulebook: Rulebook | undefined): boolean {
  return !!(
    rulebook &&
    (rulebook.timeoutMinutes != null ||
      rulebook.pausePerTeamMinutes != null ||
      rulebook.pausePerGameMinutes != null ||
      rulebook.pauseBetweenGamesMinutes != null ||
      rulebook.pauseRules ||
      rulebook.rehostRules ||
      (rulebook.bannedChampionIds?.length ?? 0) > 0 ||
      (rulebook.bannedRuneIds?.length ?? 0) > 0 ||
      rulebook.attachmentUrl)
  );
}

/** KAN-957: пуст ли регламент — чтобы не рисовать пустую карточку «Правила». Кэш общий с блоком. */
export function useRulebookHasContent(tournamentId: string, enabled: boolean) {
  const { data, isLoading } = useQuery({
    queryKey: rulebookQueryKey(tournamentId),
    queryFn: () => tournamentApi.getRulebook(tournamentId),
    enabled,
  });
  return { hasContent: rulebookHasContent(data), isLoading: enabled && isLoading };
}

export interface TournamentRulebookOverviewBlockProps {
  tournamentId: string;
}

export function TournamentRulebookOverviewBlock({ tournamentId }: TournamentRulebookOverviewBlockProps) {
  const locale = useLocale();
  const { data: rulebook, isLoading } = useQuery({
    queryKey: rulebookQueryKey(tournamentId),
    queryFn: () => tournamentApi.getRulebook(tournamentId),
  });
  const { data: champions } = useChampions();

  const championNameById = (id: number): string | undefined => {
    const champ = champions?.find((c) => c.key === String(id) || c.id === String(id));
    return champ?.name;
  };
  const championKeyById = (id: number): string | undefined => {
    const champ = champions?.find((c) => c.key === String(id) || c.id === String(id));
    return champ?.key;
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    );
  }

  if (!rulebook || !rulebookHasContent(rulebook)) return null;

  return (
    <div className="space-y-4 pb-2">
      <RulebookDisplayCards
        rulebook={rulebook}
        championNameById={championNameById}
        championKeyById={championKeyById}
        locale={locale}
      />
    </div>
  );
}
